import {css, StyleSheet} from 'aphrodite';
import React, {useCallback, useMemo} from 'react';
import {Node, XYPosition} from 'reactflow';
import {v4 as uuidv4} from 'uuid';

import isArchived from 'archive/isArchived';
import {
  ProcessExecutionType,
  ProcessFlowchartNodeDetailsType,
} from 'graphql-client/__generated/graphqlGlobalTypes';
import notifications from 'notifications';
import {
  createPlaceholderNodeForTaskSchemaCreate,
  createTaskNode,
} from 'processes/graph-builder-processes/flowchart-viz/horizontal-flowchart/nodes/nodeUtils';
import {
  NodeCreationContext,
  ProcessFlowchartTaskSchemaNodeAdapter,
} from 'processes/graph-builder-processes/flowchart-viz/horizontal-flowchart/nodes/ProcessFlowchartNodeAdapter';
import {PLUGIN_REGISTRY_INSTANCE} from 'processes/graph-builder-processes/platform/PluginRegistry';
import {isFlowchartTaskSchema} from 'processes/processes-platform/isFlowchartTaskSchema';
import {BenchlingIconName} from 'ui-library/icons/BenchlingIcon';
import {BenchlingNode} from 'ui-library/reactflow/nodes/BenchlingNode/BenchlingNode';

import {ProcessTaskSchemaWithoutFlowchartConfigGraphQL} from '../../../../../../graphql-fragments/processes/schemas/__generated/ProcessTaskSchemaWithoutFlowchartConfigGraphQL';
import {generateTaskSchemaNodeIdentifier} from '../flowchartIdentifierUtils';
import {
  FlowchartNode,
  TaskDefinitionVersionSpecWithTaskSchema,
  TaskSchemaNodeData,
} from '../processFlowchartConfigEditorNodeTypes';
import {
  createStaticNodeDataFromTaskDefinitionVersion,
  createStaticNodeDataFromTaskDefinitionVersionSpec,
  createInteractiveNodeFromStaticData,
  FlowchartOptions,
} from '../processFlowchartConfigEditorUtils';
import {SingletonTaskSchemasContext} from '../SingletonTaskSchemasContext';
import {ConfigurableTaskDefinitionGQL} from '../useAllSchemasById';
import {NodeSidepanelSectionProps} from './NodeSidepanelSection';
import {DraggableNode} from './sidepanelNodeUtils';

function DraggableTaskNode(props: {
  taskDefinition: ConfigurableTaskDefinitionGQL;
  nodeId: string;
  addNode: (node: Node<TaskSchemaNodeData>) => void;
  options: FlowchartOptions;
  reactFlowWrapper: React.RefObject<HTMLDivElement>;
  nodeDetailsType?: ProcessFlowchartNodeDetailsType;
}): JSX.Element {
  const nodeDetailsType = props.nodeDetailsType ?? ProcessFlowchartNodeDetailsType.TASK_NODE_DETAILS;
  // Note: We intentionally do NOT pass existingNodeNames here.
  // Node name incrementing should only happen when a node is actually added to the canvas,
  // not when displaying the node in the sidepanel.
  const staticNodeData = useMemo(
    () =>
      createStaticNodeDataFromTaskDefinitionVersion({
        nodeClientId: props.nodeId,
        taskDefinitionVersion: props.taskDefinition.latestVersion,
        nodeDetailsType,
        allTaskSchemas: [props.taskDefinition.latestVersion.processTaskSchema],
      }),
    [props.nodeId, props.taskDefinition.latestVersion, nodeDetailsType]
  );

  const {addNode, options} = props;

  const addTaskNode = useCallback(
    (position: XYPosition) => {
      // We need to generate a new ID for the node so that every new node of the same task schema has a unique ID.
      const newNode = createTaskNode({...staticNodeData, id: uuidv4()}, position, options);
      addNode(newNode);
    },
    [addNode, options, staticNodeData]
  );

  const nodeRenderer = useCallback(
    ({dragging}: {dragging: boolean}) => (
      <BenchlingNode.Standard.Node
        headerProps={{
          title: staticNodeData.nodeName,
          subTitle: staticNodeData.nodeSecondaryInfo,
          leftIcon: staticNodeData.nodeIcon,
        }}
      />
    ),
    [staticNodeData.nodeName, staticNodeData.nodeSecondaryInfo, staticNodeData.nodeIcon]
  );

  return (
    <DraggableNode
      addNode={addTaskNode}
      nodeRenderer={nodeRenderer}
      reactFlowWrapper={props.reactFlowWrapper}
      dataTestAttrs={{
        component: 'TaskNodeSidepanelSection',
        element: 'DraggableTaskNode',
        key: props.nodeDetailsType
          ? `${props.nodeDetailsType}-${props.taskDefinition.id}`
          : props.taskDefinition.id,
      }}
    />
  );
}

export function getTaskSchemaCreationContext({
  singletonTaskSchemasByName,
  executionType,
  rootTaskSchema,
  existingNodeNames,
}: {
  singletonTaskSchemasByName: Record<string, ProcessTaskSchemaWithoutFlowchartConfigGraphQL>;
  executionType: ProcessExecutionType;
  rootTaskSchema: ProcessTaskSchemaWithoutFlowchartConfigGraphQL;
  existingNodeNames?: Array<string>;
}): {
  adapter: ProcessFlowchartTaskSchemaNodeAdapter<unknown, unknown>;
  nodeCreationContext: NodeCreationContext;
  nodeName: string;
  nodeSubtitle: string | null;
  nodeIcon: BenchlingIconName | null;
} {
  const adapter = PLUGIN_REGISTRY_INSTANCE.getTaskSchemaNodeAdapter(executionType);

  // For some execution types, we need to use a singleton task schema.
  // If the adapter has no singleton task schema, we'll likely create a new task schema of the execution type.
  const maybeSingletonTaskSchemaName = adapter.getSingletonTaskSchemaName(rootTaskSchema);
  const maybeSingletonTaskSchema = maybeSingletonTaskSchemaName
    ? (singletonTaskSchemasByName[maybeSingletonTaskSchemaName] ?? null)
    : null;

  const nodeCreationContext: NodeCreationContext = {
    rootTaskSchema,
    singletonTaskSchemaForExecutionType: maybeSingletonTaskSchema,
    existingNodeNames,
  };

  const {nodeName, nodeSubtitle, nodeIcon} = adapter.getNewNodeDetails(nodeCreationContext);

  return {
    adapter,
    nodeCreationContext,
    nodeName,
    nodeSubtitle,
    nodeIcon,
  };
}

export async function getNodeForCreateNewTaskSchema({
  adapter,
  nodeName,
  nodeCreationContext,
  position,
  executionType,
  options,
}: {
  adapter: ProcessFlowchartTaskSchemaNodeAdapter<unknown, unknown>;
  nodeName: string;
  nodeCreationContext: NodeCreationContext;
  position: XYPosition;
  executionType: ProcessExecutionType;
  options: FlowchartOptions;
}): Promise<FlowchartNode | null> {
  let newNode: FlowchartNode;
  if (nodeCreationContext.singletonTaskSchemaForExecutionType == null) {
    // If the execution type is not associated with a singleton task schema, then the user needs to create
    // a new task schema. So we add a placeholder node to the canvas which will take the place of the
    // new node.
    newNode = createPlaceholderNodeForTaskSchemaCreate(nodeName, executionType, position, options);
  } else {
    let taskDefinitionVersionSpec: TaskDefinitionVersionSpecWithTaskSchema | null = null;
    try {
      taskDefinitionVersionSpec = await adapter.onCreateNewNode(nodeCreationContext);
      // eslint-disable-next-line benchlint/always-throw-from-catch
    } catch (error) {
      notifications.error(
        'Error creating node, please reach out to support@benchling.com for more information.'
      );
      return null;
    }

    if (!taskDefinitionVersionSpec) {
      // If taskDefinitionVersionSpec is null, user cancelled creation - don't add node or show error
      return null;
    }

    const nodeId = uuidv4();

    const staticNodeData = createStaticNodeDataFromTaskDefinitionVersionSpec({
      nodeClientId: nodeId,
      taskDefinitionVersionSpec,
      taskSchemaId: taskDefinitionVersionSpec.newProcessTaskDefinitionVersion.taskSchemaId,
      nodeDetailsType: ProcessFlowchartNodeDetailsType.TASK_NODE_DETAILS,
      existingNodeNames: options.existingNodeNames,
      isEditingNodeName: true,
    });

    newNode = createInteractiveNodeFromStaticData(staticNodeData, position, options);
  }

  return newNode;
}

function DraggableCreateNode(props: {
  executionType: ProcessExecutionType;
  addNodeToCanvas: (node: FlowchartNode) => void;
  options: FlowchartOptions;
  rootTaskSchema: ProcessTaskSchemaWithoutFlowchartConfigGraphQL;
  reactFlowWrapper: React.RefObject<HTMLDivElement>;
}): JSX.Element {
  const singletonTaskSchemasByName = SingletonTaskSchemasContext.useContext();

  const {adapter, nodeCreationContext, nodeName, nodeSubtitle, nodeIcon} = useMemo(
    () =>
      getTaskSchemaCreationContext({
        singletonTaskSchemasByName,
        executionType: props.executionType,
        rootTaskSchema: props.rootTaskSchema,
        existingNodeNames: props.options.existingNodeNames,
      }),
    [props.executionType, props.rootTaskSchema, props.options.existingNodeNames, singletonTaskSchemasByName]
  );

  const {addNodeToCanvas, executionType, options} = props;

  const handleCreateNode = useCallback(
    async (position: XYPosition) => {
      const newNode = await getNodeForCreateNewTaskSchema({
        adapter,
        nodeName,
        nodeCreationContext,
        position,
        executionType,
        options,
      });
      if (newNode != null) {
        addNodeToCanvas(newNode);
      }
    },
    [addNodeToCanvas, executionType, options, adapter, nodeCreationContext, nodeName]
  );

  const nodeRenderer = useCallback(
    () => (
      <BenchlingNode.Standard.Node
        headerProps={{
          title: nodeName,
          subTitle: nodeSubtitle ?? undefined,
          leftIcon: nodeIcon ?? undefined,
        }}
      />
    ),
    [nodeName, nodeSubtitle, nodeIcon]
  );

  return (
    <DraggableNode
      addNode={handleCreateNode}
      nodeRenderer={nodeRenderer}
      reactFlowWrapper={props.reactFlowWrapper}
      dataTestAttrs={{
        component: 'TaskNodeSidepanelSection',
        element: 'DraggableCreateNode',
        key: props.executionType,
      }}
    />
  );
}

export type TaskNodeSidepanelSectionProps = Omit<NodeSidepanelSectionProps, 'section' | 'nodeTypes'>;

function findRootTaskSchemaDefinition(
  taskDefinitionsForSidepanel: Array<ConfigurableTaskDefinitionGQL>,
  rootTaskSchemaId: string
): ConfigurableTaskDefinitionGQL | null {
  return (
    taskDefinitionsForSidepanel.find((def) => def.latestVersion.processTaskSchema.id === rootTaskSchemaId) ??
    null
  );
}

/**
 * A sidepanel component that displays all available task nodes that can be added to the flowchart
 */
export default function TaskNodeSidepanelSection(
  props: Omit<NodeSidepanelSectionProps, 'section' | 'nodeTypes'>
): JSX.Element {
  const allowedExecutionTypes = props.flowchartEditorConfigurator.allowedNodeExecutionTypes;

  const flowchartOrganization = props.rootTaskSchema.organization;

  const rootTaskSchemaDefinition = props.rootAndOutputNodeConfig
    ? findRootTaskSchemaDefinition(props.taskDefinitionsForSidepanel, props.rootTaskSchema.id)
    : null;

  const showStartNode = rootTaskSchemaDefinition != null && !props.rootAndOutputNodeConfig?.hasStartNode;
  const showEndNode =
    rootTaskSchemaDefinition != null &&
    !props.rootAndOutputNodeConfig?.hasEndNode &&
    props.rootTaskSchema.outputSchema != null;

  const filteredTaskDefinitions = props.taskDefinitionsForSidepanel
    .filter(
      (definition) =>
        !isArchived(definition.latestVersion.processTaskSchema) &&
        !isFlowchartTaskSchema(definition.latestVersion.processTaskSchema) &&
        flowchartOrganization.id === definition.latestVersion.processTaskSchema.organization.id &&
        (props.searchQuery == null ||
          definition.latestVersion.processTaskSchema.name
            .toLowerCase()
            .includes(props.searchQuery.toLowerCase()))
    )
    .toSorted((a, b) =>
      a.latestVersion.processTaskSchema.name.localeCompare(b.latestVersion.processTaskSchema.name)
    );

  return (
    <div className={css(styles.taskNodesContainer)}>
      {/* Create New Nodes Section */}
      {allowedExecutionTypes
        .filter((executionType) => !isFlowchartTaskSchema({executionType}))
        .map((executionType) => (
          <DraggableCreateNode
            key={`create-${executionType}`}
            executionType={executionType}
            addNodeToCanvas={props.addNodeToCanvas}
            options={props.options}
            rootTaskSchema={props.rootTaskSchema}
            reactFlowWrapper={props.reactFlowWrapper}
          />
        ))}

      {allowedExecutionTypes.length > 0 &&
        (showStartNode || showEndNode || filteredTaskDefinitions.length > 0) && (
          <div className={css(styles.separator)} />
        )}

      {(showStartNode || showEndNode) && rootTaskSchemaDefinition != null && (
        <>
          {showStartNode && (
            <DraggableTaskNode
              key={`root-${rootTaskSchemaDefinition.id}`}
              nodeId={`root-${generateTaskSchemaNodeIdentifier(rootTaskSchemaDefinition.latestVersion.processTaskSchema)}`}
              taskDefinition={rootTaskSchemaDefinition}
              addNode={props.addNodeToCanvas}
              options={props.options}
              reactFlowWrapper={props.reactFlowWrapper}
              nodeDetailsType={ProcessFlowchartNodeDetailsType.ROOT_NODE_DETAILS}
            />
          )}
          {showEndNode && (
            <DraggableTaskNode
              key={`output-${rootTaskSchemaDefinition.id}`}
              nodeId={`output-${generateTaskSchemaNodeIdentifier(rootTaskSchemaDefinition.latestVersion.processTaskSchema)}`}
              taskDefinition={rootTaskSchemaDefinition}
              addNode={props.addNodeToCanvas}
              options={props.options}
              reactFlowWrapper={props.reactFlowWrapper}
              nodeDetailsType={ProcessFlowchartNodeDetailsType.OUTPUT_NODE_DETAILS}
            />
          )}
        </>
      )}

      {/* Existing Task Definitions */}
      {filteredTaskDefinitions.map((definition) => {
        const nodeId = generateTaskSchemaNodeIdentifier(definition.latestVersion.processTaskSchema);
        return (
          <DraggableTaskNode
            key={nodeId}
            nodeId={nodeId}
            taskDefinition={definition}
            addNode={props.addNodeToCanvas}
            options={props.options}
            reactFlowWrapper={props.reactFlowWrapper}
          />
        );
      })}
    </div>
  );
}

const styles = StyleSheet.create({
  taskNodesContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    width: '100%',
  },
  separator: {
    width: '100%',
    height: '1px',
    backgroundColor: '#eee',
    margin: '10px 0',
  },
});
